<template>
  <PanelLayout
    id="employee-panel"
    :loading="loading || saving"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="onCancel"
    :unsavedChanges="unsavedChanges"
    @cancel="onCancel"
    @save="onSave"
  >
    <template #actions-right>
      <div v-if="employeeData" class="flex items-center space-x-2">
        <OHistoryLink
          :item="employeeData"
          model="Employee"
          icon="history"
          slim
          flat
        />
      </div>
    </template>

    <template #heading>
      <div v-if="employeeData" class="flex items-center space-x-2">
        <div>
          <h2 class="text-lg md:text-xl tracking-tight font-bold leading-none/85">
            {{ employeeData.isNewRecord ? $t('employee.new') : employeeData.displayName }}
          </h2>
          <div v-if="!employeeData.isNewRecord" class="mt-1">
            <p class="opacity-80 text-sm md:text-base" v-html="$t('pages.employee.edit.id', { id: employeeData.idEmployee })" />
          </div>
        </div>
      </div>
    </template>

    <div v-if="employeeData" class="h-full">
      <OTabs ref="tabsRef" nav-class="bg-gray-50 h-11 border-b dark:bg-gray-900 dark:border-gray-800">
        <OTab :name="$t('general.details')" icon="customerEdit" scroll>
          <EmployeeDetails v-model:employee="employeeData" @update="onUpdate" />
        </OTab>
        <OTab :name="$t('general.alerts')" icon="notification" scroll>
          <EmployeeNotifications v-model:employee="employeeData" />
        </OTab>
        <OTab :name="$t('pages.employee.edit.serviceOptions')" icon="services">
          <EmployeeServices
            v-model:employee="employeeData"
            :handleServicePriceChange="onServicePriceChange"
          />
        </OTab>
        <OTab :name="$t('minimiseGaps.title')" icon="minimiseGaps" scroll>
          <EmployeeMinimiseGaps v-model:employee="employeeData" />
        </OTab>
        <OTab
          :is-visible="!employeeData.isNewRecord && !!rosterData"
          :name="$t('general.roster')"
          icon="roster"
          :initial="props.tab === 'roster'"
          scroll
        >
          <EmployeeRoster v-if="rosterData" v-model:employee="employeeData" v-model:roster="rosterData" />
        </OTab>
      </OTabs>
    </div>

    <template v-if="employeeData">
      <UpdatePricesDialog
        ref="updatePricesDialogRef"
      />
    </template>
  </PanelLayout>
</template>

<script setup lang="ts">
import EmployeeDetails from '@/components/employee/edit/Details.vue'
import EmployeeNotifications from '@/components/employee/edit/Notifications.vue'
import EmployeeServices from '@/components/employee/edit/Services.vue'
import EmployeeMinimiseGaps from '@/components/employee/edit/MinimiseGaps.vue'
import EmployeeRoster from '@/components/employee/edit/Roster.vue'

// @ts-ignore - No types available for this module
import UpdatePricesDialog from '@/components/employee/actions/UpdatePrices.vue'

// @ts-ignore - No types available for this module  
import PanelLayout from '@/components/panel/PanelLayout.vue'

import { OTabs, OTab } from '@/components/elements'

import type { SecondaryModelConfig } from '@/composables/useEditModels'

import type { Employee } from '~/_api/models/employee'
import type { RosterEmployeeDefault } from '~/_api/models/roster'

import { usePanel } from '~/composables/usePanel'

const props = defineProps<{ id?: string; tab?: string }>()

// Use Nuxt app composables
const { $api, $notification, $bus, $i18n, $dialog } = useNuxtApp()

// Template refs
const panel = usePanel()
const tabsRef = ref()
const updatePricesDialogRef = ref()

const hasServicePriceChanged = ref<boolean>(false)

// ── Models ──────────────────────────────────────────────────────────────────

const {
  data: employeeData,
  models: { roster },
  loading,
  saving,
  changed,
  onSubmit,
} = useEditModels<Employee, {
  roster: SecondaryModelConfig<RosterEmployeeDefault, Employee>
}>({
  primary: {
    id: props.id,
    service: $api.employee,
    successNotification: false,
    onAfterLoad: (data: Employee) => {
      if (data?.services) {
        data.services.sort((a, b) => a?.service.listOrder - b?.service.listOrder)
      }
    },
    onAfterSubmit: (response: any, data: Employee) => {
      if (hasServicePriceChanged.value) {
        updatePricesDialogRef.value?.open(data)
      }

      $notification.toast({
        title: data.isNewRecord ? 'Employee created successfully' : 'Employee updated successfully',
        text: 'Employee information has been saved',
        variant: 'success',
      })

      $bus.emit.updated('Employee', data)
    },
  },
  secondary: {
    roster: {
      fetch: async (employee: Employee) => {
        const response = await $api.roster.employeeDefault(employee)
        return response.data
      },
      save: async (data: RosterEmployeeDefault, employee: Employee) => {
        await $api.roster.saveEmployeeDefault(employee, data)
      },
      onAfterSave: (data: RosterEmployeeDefault) => {
        $bus.emit.updated('Roster', data)
      },
    },
  },
})

// Writable computed to unwrap roster.data ref for Vue 2 template compatibility
// (Vue 2 does not auto-unwrap Refs nested inside plain objects in templates)
const rosterData = computed<RosterEmployeeDefault | null>({
  get: () => roster.data.value,
  set: (val: RosterEmployeeDefault | null) => { roster.data.value = val },
})

// ── Panel ────────────────────────────────────────────────────────────────────

const unsavedChanges = computed<boolean>(() => changed.value)

const onCancel = (force: boolean = false): void => {
  if (!force && unsavedChanges.value) {
    if ($dialog) {
      $dialog.confirm($i18n.t('general.unsavedChangesInfo'), {
        title: $i18n.t('general.unsavedChanges'),
        confirm: {
          text: $i18n.t('general.saveAndClose'),
          callback: async () => {
            await onSubmit()
            panel?.close(true)
            return true
          },
        },
        cancel: {
          text: $i18n.t('general.closeWithoutSaving'),
          callback: () => {
            panel?.close(true)
            return true
          },
        },
      })
      return
    }

    const shouldClose = typeof window === 'undefined'
      ? true
      : window.confirm(String($i18n.t('general.unsavedChangesInfo')))
    if (shouldClose) {
      panel?.close(true)
    }
    return
  }

  panel?.close(force)
}

const onSave = async (close: boolean = false): Promise<void> => {
  await onSubmit()
  if (close) {
    panel?.close(true)
  }
}

const onServicePriceChange = (): void => {
  hasServicePriceChanged.value = true
}

const onUpdate = (): void => {
  $bus.emit.updated('Employee', employeeData.value)
}
</script>
